import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { View, Platform, PixelRatio, StyleSheet } from 'react-native';

import { COMMAND_ROUTE, USER_ROUTE, VIEWBOARD_SYNC } from 'utils/World/config';
import PlatformWorld from 'components/World/PlatformWorld';
import Loading from 'components/World/Loading';
import TextOverlayManager from 'components/World/TextOverlayManager';
import LabelDisplay from 'components/World/LabelDisplay';
import {
  startAITeacherListening,
  stopAITeacherListening,
} from 'store/communication/actions/speechToTextServiceActions';
import { disableWebboard, enableWebboard } from 'store/media';
import {
  joinMeeting,
  setAvatarURL,
  setReadyToMove,
  setDirectLanding,
  setShowConfirmationDialog,
  setCourseLeaveConfigs,
  setLessonButtonVisibility,
  setLessonButtonInteraction,
  setLessonPieceText,
  setLessonStarted,
  setLessonPaused,
  setRpmReady,
  resetLessonButtonInteraction,
} from 'store/meeting/actions/meeting';
import { setCourseProgress } from 'store/app/actions';
import { connect } from 'react-redux';
import {
  QUIKLEARN_BACKEND_URL,
  LESSON_TRACKING_API,
  API_KEY,
  WEB_APP_BASE_URL,
  ASSESSMENT_BASE_URL,
} from 'config/app.config';
import { showMessage } from 'react-native-flash-message';
import {
  showCourseCompleteMessage,
  courseComplete,
} from 'store/communication/actions/courseActions';
import { sendWebhookData } from 'utils/communication/sendWebhookData';
import {
  setCurriculumGradeAndSubject,
  setShowUnitSelectionScreen,
  setCourseData,
  setCurrentLessonProgress,
  setLastQuestion,
} from '../../store/course';
import { setWorldLoading } from 'store/world/actions';

async function updateCourseProgress({
  courseId,
  unitId,
  lessonId,
  userId,
  courseProgressState,
  legacy,
  token,
  currentLessonSlidesWatched,
  currentSlideIndex,
  currentLessonTimeSpent,
}) {
  try {
    const progressValue = parseFloat(courseProgressState);

    if (isNaN(progressValue)) {
      throw new Error('Invalid progress data: not a number');
    }

    const slidesWatchedNumeric = Math.max(
      0,
      Number(currentLessonSlidesWatched ?? 0) || 0,
    );
    const slideIndexNumeric = Math.max(
      0,
      Number(currentSlideIndex ?? (slidesWatchedNumeric - 1)) || 0,
    );

    const requestBody = legacy
      ? {
        progress: progressValue,
      }
      : {
        unitId: unitId,
        lessonId: lessonId,
        progress: progressValue,
        slides: currentLessonSlidesWatched,
        currentSlideIndex: slideIndexNumeric, // 0-based
        minutes: Math.round((currentLessonTimeSpent / 60000) * 100) / 100,
      };

    const endpoint = LESSON_TRACKING_API;
    const base = String(QUIKLEARN_BACKEND_URL || '').replace(/\/$/, '');

    const response = legacy
      ? await fetch(
        `${QUIKLEARN_BACKEND_URL}/staging/course/${courseId}/progress/${userId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        },
      )
      : await fetch(`${base}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': API_KEY,
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const result = await response.json();
    console.log(
      legacy
        ? 'Course progress updated successfully:'
        : 'Lesson progress updated successfully:',
      result,
    );
  } catch (error) {
    console.error(
      legacy
        ? 'Error updating course progress:'
        : 'Error updating lesson progress:',
      error,
    );
  }
}

class World extends Component {
  static instance = undefined;

  static AVATAR_STORAGE_KEY = 'view.currentAvatarJson';

  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      lowPower: false,
      newAvatarUrl: '',
      userId: '',
      courseId: '',
      courseProgress: '',
      isStarted: false,
      entryTime: null,
      exitTime: null,
      levelLoaded: false,
    };
  }

  // Location mapping hook (kept as a function so we can re-introduce aliases if needed).
  mapLocationAlias = (loc) => {
    return loc;
  };

  getSavedAvatarRecord = () => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return null;
    try {
      const raw = window.localStorage?.getItem(World.AVATAR_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      return parsed;
    } catch (_) {
      return null;
    }
  };

  saveAvatarRecordFromUnityPayload = (payload) => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    if (typeof payload !== 'string') return;
    if (!payload.startsWith('avatar_json_saved|')) return;

    // avatar_json_saved|key|bodyGroup|gender|b64
    const parts = payload.split('|');
    if (parts.length < 5) return;
    const key = parts[1] || '';
    const bodyGroup = parts[2] || '';
    const gender = parts[3] || '';
    const b64 = parts[4] || '';
    if (!key || !b64) return;

    const record = {key, bodyGroup, gender, b64, ts: Date.now()};
    try {
      window.localStorage?.setItem(World.AVATAR_STORAGE_KEY, JSON.stringify(record));
    } catch (_) {
      // ignore storage failures (private mode / quota)
    }
  };

  replaySavedAvatarIntoWorld = () => {
    // Must be called on web, after iframe exists, and immediately before set_avatar_url.
    const rec = this.getSavedAvatarRecord();
    if (!rec?.key || !rec?.b64) return null;

    const key = rec.key;
    const bodyGroup = rec.bodyGroup || '';
    const gender = rec.gender || '';
    const b64 = rec.b64;

    PlatformWorld.sendMessage({
      route: 'local-appearance',
      payload: `avatar_json|${key}|${bodyGroup}|${gender}|${b64}`,
    });

    return rec;
  };

  componentDidMount() {
    if (World.instance) {
      throw new Error('Cannot have multiple 3d world components mounted');
    }
    World.instance = this;
    if (this.props.legacy) {
      window.onbeforeunload = event => {
        this.handleExit();
        event.returnValue = 'Are you sure you want to leave?';
      };
    }
  }
  handleExit = () => {
    let _exitTime = this.state.exitTime;
    if (this.state.isStarted) {
      const entry = Date.now(); // Using high-resolution time measurement
      this.setState({
        exitTime: entry,
      });
      _exitTime = entry;
    }
    const timeSpent =
      _exitTime !== null && this.state.entryTime !== null
        ? (_exitTime - this.state.entryTime) / 1000
        : null;
    if (timeSpent) {
      console.log('handleExit', timeSpent);
      const payload = {
        message: 'Course Exit!',
        timestamp: Date.now() + '',
        extra: JSON.stringify({
          timeSpent: timeSpent,
          userAgent: navigator.userAgent,
          isPromotional: this.props.isPromotional,
          userId: this.props.userId,
          courseId: this.props.courseId,
          courseProgress: this.props.courseProgress,
        }),
      };
      try {
        sendWebhookData(payload); //Parth added hooks for exit course/complete
      } catch (error) {
        console.error(error);
      }
    }
  };
  componentWillUnmount() {
    World.instance = undefined;
    if (this.props.legacy) {
      this.handleExit();

      // Clean up unload listener
      window.onbeforeunload = null;
    }
  }

  shouldComponentUpdate(nextProps) {
    const { location, dispatch } = this.props;
    const currentEffectiveLocation = this.mapLocationAlias(location);
    const nextEffectiveLocation = this.mapLocationAlias(nextProps.location);

    if (nextEffectiveLocation !== currentEffectiveLocation) {
      this.setState({ loading: true });
      dispatch(setWorldLoading(true));
    }

    if (nextProps.lowPower !== this.state.lowPower) {
      this.setPowerMode(nextProps.lowPower);
    }

    return true;
  }

  static runCommand = command => {
    console.log('COMMAND ROUTE: ', COMMAND_ROUTE);
    console.log('COMMAND: ', command);

    PlatformWorld.sendMessage({
      route: COMMAND_ROUTE,
      payload: command,
    });
  };

  static onReceivePeerMessage = message => {
    if (message.route === 'j' || message.route === USER_ROUTE) {
      message.payload = `${message.peer}|${message.payload}`;
      PlatformWorld.sendMessage(message);
    } else if (message.action === 'teleport') {
      World.runCommand(`player_leave ${message.peer}`);
    } else if (message.route === VIEWBOARD_SYNC) {
      PlatformWorld.sendMessage(message);
    }
  };

  static onReceivePeerState = (peerName, state) => {
    if (
      state &&
      state.appearance &&
      typeof state.appearance !== 'undefined' &&
      Object.keys(state.appearance).length !== 0
    ) {
      PlatformWorld.sendMessage({
        route: 'd',
        payload: `${peerName}|${state.appearance}`,
      });
    }
  };

  static onUpdatePeerFloatingName = (peerId, peerDisplayName) => {
    if (peerId && peerDisplayName) {
      PlatformWorld.sendMessage({
        route: USER_ROUTE,
        payload: `${peerId}|nickname|${peerDisplayName}`,
      });
    }
  };

  filterTransformMessages = message => {
    const { onTransformFromWorld } = this.props;
    const t = message.value;
    const values = `${t.x} ${t.y} ${t.z} ${t.rx} ${t.ry} ${t.rz} ${t.time}`;
    const payload = `transform|${values}|${t.filter}`;

    onTransformFromWorld({ route: USER_ROUTE, payload });
  };

  componentDidUpdate(prevProps, prevState) {
    // Check if newAvatarUrl has changed
    if (prevState.newAvatarUrl !== this.state.newAvatarUrl) {
      const { newAvatarUrl } = this.state;
      console.log(
        'Usman new avatar url after state change ::: ' + newAvatarUrl,
      );
    }

    // If course/unit context arrives after Unity load complete, we still need to
    // explicitly turn off next-unit portal when we're on the last unit.
    const courseDataChanged = prevProps.courseData !== this.props.courseData;
    const unitChanged = prevProps.unitId !== this.props.unitId;
    const customCourseChanged = prevProps.customCourse !== this.props.customCourse;
    const nowLevelLoaded = Boolean(this.state.levelLoaded);

    if (nowLevelLoaded && (courseDataChanged || unitChanged || customCourseChanged)) {
      this.ensureNextUnitPortalState('componentDidUpdate');
    }
  }

  getActiveUnitIndexFromCourseData = () => {
    const { courseData, unitId } = this.props;
    const units = courseData?.units || [];
    if (!Array.isArray(units) || units.length === 0) return -1;

    const normalize = (s = '') =>
      (s || '')
        .toString()
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '');
    const getUnitId = u => u?.id ?? u?.unitId ?? u?.unitID ?? u?.unit_id ?? null;

    // Prefer explicit token unit id match (handles mid-course teleports reliably)
    if (unitId) {
      const target = normalize(unitId);
      const byId = units.findIndex(u => normalize(getUnitId(u)) === target);
      if (byId >= 0) return byId;
    }

    // Fallback: backend-provided index (only if it looks sane)
    const fromCourseData = Number(courseData?.currentUnitIndex);
    if (Number.isFinite(fromCourseData) && fromCourseData >= 0) return fromCourseData;

    return -1;
  };

  isLastUnitLoaded = () => {
    const { courseData } = this.props;
    const units = courseData?.units || [];
    if (!Array.isArray(units) || units.length === 0) return false;
    const idx = this.getActiveUnitIndexFromCourseData();
    return idx >= 0 && idx === units.length - 1;
  };

  ensureNextUnitPortalState = (reason = '') => {
    // NOTE: Portal commands are currently only dispatched in the web path.
    if (Platform.OS !== 'web') return;

    const { customCourse } = this.props;
    const shouldTurnOff = Boolean(customCourse) || this.isLastUnitLoaded();
    if (!shouldTurnOff) return;

    console.log('[World] turnoff_next_unit_portal', {
      reason,
      customCourse: Boolean(customCourse),
      unitId: this.props.unitId,
      activeUnitIndex: this.getActiveUnitIndexFromCourseData(),
      totalUnits: this.props.courseData?.units?.length || 0,
      nextUnitId: this.props.courseData?.nextUnitId ?? null,
    });

    PlatformWorld.sendMessage({
      route: COMMAND_ROUTE,
      payload: 'turnoff_next_unit_portal true',
    });
  };

  filterLegacyMessages = message => {
    const { onLegacyFromWorld } = this.props;
    const { route, payload } = message;
    // console.log('filterLegacyMessages', route, payload);

    if (route === 'x') {
      this.onLevelLoadComplete();
    } else if (route === 'p') {
      // ignore the old/erroneous command that sends a number with the p. In that case the payload will be undefined.
      if (!payload) {
        return;
      }
      const player_variables = payload.split('|');
      if (player_variables[0] === 'rpmReady') {
        console.log('Usman RPM ready recieved');
        this.setState({ loading: false });
        const { dispatch } = this.props;
        dispatch(setWorldLoading(false));
        dispatch(setRpmReady(true));
      }
      if (player_variables[0] === 'updatedAppearance') {
        // player var for knowing when unity has finished updating an avatar.
        if (player_variables[1] === 'System.Object') {
          // This particular variable is a false variable that Unity sends on initialization. We need to ignore it here.
          return;
        }
        // this is a completed avatar appearance. Update local state and send the message through to be processed.
        this.setState({ loading: false });
        const { dispatch } = this.props;
        dispatch(setWorldLoading(false));
        onLegacyFromWorld(message);
      }
    } else if (route === 'c') {
      // Character Lab: Unity saved/updated the full appearance JSON. Persist it in parent storage.
      // Contract: avatar_json_saved|<key>|<bodyGroup>|<gender:m|f>|<base64(utf8(json))>
      if (typeof payload === 'string' && payload.startsWith('avatar_json_saved|')) {
        this.saveAvatarRecordFromUnityPayload(payload);
        return;
      }

      // New lesson panel / transcript commands from Unity (may be space-separated or pipe-delimited)
      if (typeof payload === 'string') {
        if (payload.startsWith('set_lesson_button_visibility')) {
          try {
            // Unity may send either pipe-delimited or space-delimited:
            // - set_lesson_button_visibility|pause|True
            // - set_lesson_button_visibility pause True
            const parts = payload.includes('|')
              ? payload.split('|')
              : payload.trim().split(/\s+/);
            let buttonType = (parts[1] || '').toLowerCase();
            const visibleRaw = (parts[2] || '').toLowerCase();

            if (buttonType === 'prev') buttonType = 'previous';
            const visible =
              visibleRaw === 'true' || visibleRaw === '1' || visibleRaw === 'yes';

            const { dispatch } = this.props;
            if (['next', 'previous', 'pause', 'resume'].includes(buttonType)) {
              // Avoid dispatching the same visibility state repeatedly (Unity can emit per-frame)
              const currentVisibleRaw =
                this.props.lessonControls?.buttonVisibility?.[buttonType];
              // Meeting reducer forces next/previous visible regardless of Unity payload.
              const nextVisible =
                buttonType === 'next' || buttonType === 'previous'
                  ? true
                  : Boolean(visible);
              const currentVisible =
                buttonType === 'next' || buttonType === 'previous'
                  ? true
                  : Boolean(currentVisibleRaw);
              if (currentVisible !== nextVisible) {
                dispatch(setLessonButtonVisibility({ buttonType, visible }));
              }

              // Keep lessonPaused in sync with Unity's pause/resume visibility contract
              // - pause visible => lesson playing (not paused)
              // - resume visible => lesson paused
              if (buttonType === 'pause') {
                const nextPaused = !nextVisible;
                if (Boolean(this.props.lessonPaused) !== Boolean(nextPaused)) {
                  dispatch(setLessonPaused(nextPaused));
                }
              } else if (buttonType === 'resume') {
                const nextPaused = nextVisible;
                if (Boolean(this.props.lessonPaused) !== Boolean(nextPaused)) {
                  dispatch(setLessonPaused(nextPaused));
                }
              }
            }
          } catch (e) {
            console.warn(
              '[World] Failed to parse set_lesson_button_visibility payload from Unity:',
              payload,
              e,
            );
          }
          return;
        }

        if (payload.startsWith('set_lesson_button_interaction')) {
          try {
            // Unity may send either pipe-delimited or space-delimited:
            // - set_lesson_button_interaction|pause|True
            // - set_lesson_button_interaction pause True
            const parts = payload.includes('|')
              ? payload.split('|')
              : payload.trim().split(/\s+/);
            let buttonType = (parts[1] || '').toLowerCase();
            const interactRaw = (parts[2] || '').toLowerCase();

            if (buttonType === 'prev') buttonType = 'previous';
            const interactable =
              interactRaw === 'true' ||
              interactRaw === '1' ||
              interactRaw === 'yes';

            const { dispatch } = this.props;
            if (['next', 'previous', 'pause', 'resume'].includes(buttonType)) {
              // Avoid dispatching the same interaction state repeatedly (Unity can emit per-frame)
              const enabled = Boolean(interactable);
              if (buttonType === 'pause' || buttonType === 'resume') {
                const currentEnabled = Boolean(
                  this.props.lessonControls?.buttonInteraction?.pause,
                );
                if (currentEnabled !== enabled) {
                  dispatch(
                    setLessonButtonInteraction({ buttonType, interactable }),
                  );
                }
              } else {
                const currentEnabled = Boolean(
                  this.props.lessonControls?.buttonInteraction?.[buttonType],
                );
                if (currentEnabled !== enabled) {
                  dispatch(
                    setLessonButtonInteraction({ buttonType, interactable }),
                  );
                }
              }
            }
          } catch (e) {
            console.warn(
              '[World] Failed to parse set_lesson_button_interaction payload from Unity:',
              payload,
              e,
            );
          }
          return;
        }

        if (payload.startsWith('set_lesson_piece_text_command')) {
          try {
            // Everything after the command token is lesson text.
            const raw = payload.substring('set_lesson_piece_text_command'.length);
            let textRaw = raw;
            if (
              textRaw.startsWith(' ') ||
              textRaw.startsWith('|') ||
              textRaw.startsWith(':')
            ) {
              textRaw = textRaw.substring(1);
            }
            const text = textRaw.trim();
            const { dispatch } = this.props;
            // Avoid dispatching duplicate transcript text (Unity can emit per-frame)
            if ((this.props.lessonControls?.lessonText || '') !== text) {
              const lessonId = this.props.currentLessonProgress?.currentLessonId || '';
              if (process.env.NODE_ENV !== 'production') {
                // Debug: helps confirm whether Unity actually emits piece text on same-lesson restart.
                console.log('[World] lesson piece text update', {
                  lessonId,
                  len: (text || '').length,
                });
              }
              dispatch(setLessonPieceText({ text, lessonId }));
            }
          } catch (e) {
            console.warn(
              '[World] Failed to parse set_lesson_piece_text_command payload from Unity:',
              payload,
              e,
            );
          }
          return;
        }
      }

      const payload_variables = payload.split('|');
      if (payload === 'startAITeacherListening') {
        const { dispatch } = this.props;
        dispatch(startAITeacherListening());
      } else if (payload === 'stopAITeacherListening') {
        const { dispatch } = this.props;
        dispatch(stopAITeacherListening());
      } else if (payload_variables[0] === 'setWebboardUrl') {
        var showStatus = payload_variables[2];
        if (showStatus === 'show') {
          var url = payload_variables[1];
          const { dispatch } = this.props;
          dispatch(enableWebboard(payload_variables[1]));
        } else if (showStatus === 'hide') {
          const { dispatch } = this.props;
          dispatch(disableWebboard());
        }
      } else if (payload === 'joinMeeting') {
        const { dispatch } = this.props;
        // Web-only: once Unity signals "joinMeeting" (e.g. after avatar save/customize),
        // we consider avatar selection complete. React does not navigate by changing the URL,
        // so keep the URL params in sync for debugging / refresh behavior.
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          try {
            // avatarSelection=false => direct_landing=true
            dispatch(setDirectLanding(true));
            const current = new URL(window.location.href);
            current.searchParams.set('avatarSelection', 'false');
            window.history.replaceState(null, '', current.toString());
          } catch (_) {
            // ignore URL parsing failures (older browsers / unexpected href)
          }
        }
        dispatch(joinMeeting(true));
      } else if (payload === 'ready_to_move') {
        const { dispatch } = this.props;
        dispatch(setReadyToMove(true));
        // Reset lesson flags when a new world becomes ready
        dispatch(setLessonStarted(false));
        dispatch(setLessonPaused(false));
        dispatch(resetLessonButtonInteraction());
      } else if (payload_variables[0] === 'avatarurl') {
        var avatarUrl = payload_variables[1];
        const { dispatch } = this.props;
        dispatch(setAvatarURL(avatarUrl));
        this.setState({ newAvatarUrl: avatarUrl });
        const current = new URL(window.location.href);

        current.searchParams.set('avatarUrl', avatarUrl);

        window.history.replaceState(null, '', current.toString());
      }
      // this is the course state shared from unity side along with video index so use it according to your need
      else if (payload_variables[0] === 'courseState') {
        var videoIndex = payload_variables[1];
        const { dispatch, legacy, currentLessonProgress, token, unitId } =
          this.props;
        console.log('courseState recieved from Unity: ' + videoIndex);
        if (legacy) {
          dispatch(setCourseProgress(videoIndex));
          updateCourseProgress({
            courseId: this.props.courseId,
            userId: this.props.userId,
            courseProgressState: videoIndex,
            legacy: legacy,
          });
          this.setState({ courseProgress: videoIndex });
          console.log(
            'this.state.courseProgress: ' + this.state.courseProgress,
          );
        } else {
          const videoIndexInt = Number.parseInt(String(videoIndex), 10);
          // Unity `courseState` is treated as the CURRENT slide index (0-based).
          const unitySlideIndexRaw = Number.isFinite(videoIndexInt) ? videoIndexInt : 0;
          const totalSlides = Number(currentLessonProgress?.currentLessonTotalSlides ?? 0) || 0;
          const unitySlideIndex = Math.max(
            0,
            totalSlides > 0 ? Math.min(totalSlides - 1, unitySlideIndexRaw) : unitySlideIndexRaw,
          );
          const slidesViewedFromUnity = unitySlideIndex + 1; // count (1-based)

          // We will only set progress if it is more than last value
          let progressPercentage = Math.max(
            Math.round(
              (slidesViewedFromUnity / Math.max(1, totalSlides)) * 100,
            ).toString(),
            currentLessonProgress.currentLessonProgressPercentage,
          );
          // If we have already hit 100% earlier, don't resync here; only sync in lessonEnded
          if (
            parseInt(progressPercentage, 10) >= 100 ||
            currentLessonProgress.currentLessonProgressPercentage >= 100
          ) {
            progressPercentage =
              currentLessonProgress.currentLessonProgressPercentage;
          }

          const previousWatched = Number(
            this.props.currentLessonProgress.currentLessonSlidesWatched ?? 0,
          ) || 0;
          const totalSlidesWatched = Math.max(previousWatched, slidesViewedFromUnity);
          const currentSlideIndexFromUnity = unitySlideIndex;

          // Ensure unitId is valid; fallback to current courseData unit if missing/unknown
          const { courseData } = this.props;
          let unitIdToSend = this.props.unitId;
          if (!unitIdToSend || unitIdToSend === 'unknownUnit') {
            try {
              const idx = courseData?.currentUnitIndex ?? 0;
              unitIdToSend =
                courseData?.units?.[idx]?.id ||
                courseData?.units?.[0]?.id ||
                unitIdToSend;
              if (!unitIdToSend || unitIdToSend === 'unknownUnit') {
                console.warn(
                  '[updateCourseProgress] Missing unitId; using fallback failed',
                );
              } else {
                console.log(
                  '[updateCourseProgress] Using fallback unitId from courseData:',
                  unitIdToSend,
                );
              }
            } catch (_) {
              console.warn('[updateCourseProgress] Failed to derive fallback unitId');
            }
          }

          // Avoid promoting to 100% here; 100% is finalized by lessonEnded only
          dispatch(
            setCurrentLessonProgress({
              currentLessonId: this.props.currentLessonProgress.currentLessonId,
              currentLessonProgressPercentage: progressPercentage,
              currentLessonTotalSlides:
                this.props.currentLessonProgress.currentLessonTotalSlides,
              currentLessonSlidesWatched: totalSlidesWatched,
            }),
          );

          // Sync progress with new backend, but do not send 100% here; defer to lessonEnded
          const pctInt = parseInt(progressPercentage, 10);
          if (isFinite(pctInt) && pctInt < 100) {
            updateCourseProgress({
              courseId: this.props.courseId,
              unitId: unitIdToSend,
              lessonId: this.props.currentLessonProgress.currentLessonId,
              userId: this.props.userId,
              courseProgressState: pctInt,
              legacy: legacy,
              token: token,
              currentLessonSlidesWatched: totalSlidesWatched,
              currentSlideIndex: currentSlideIndexFromUnity,
              currentLessonTimeSpent:
                Date.now() -
                this.props.currentLessonProgress.currentLessonStartTimeStamp,
            }).catch(e =>
              console.warn(
                '[updateCourseProgress] tick failed',
                e?.message || e,
              ),
            );
          }
        }
      } else if (payload_variables[0] === 'lessonEnded') {
        const { dispatch } = this.props;
        dispatch(showCourseCompleteMessage());
        dispatch(courseComplete());
        dispatch(setLessonStarted(false));
        dispatch(setLessonPaused(false));
        if (!this.props.legacy) {
          dispatch(
            setCurrentLessonProgress({
              currentLessonId: this.props.currentLessonProgress.currentLessonId,
              currentLessonProgressPercentage: 100,
              currentLessonTotalSlides:
                this.props.currentLessonProgress.currentLessonTotalSlides,
            }),
          );

          // Ensure unitId is valid; fallback to current courseData unit if missing/unknown
          const { courseData } = this.props;
          let unitIdToSend = this.props.unitId;
          if (!unitIdToSend || unitIdToSend === 'unknownUnit') {
            try {
              const idx = courseData?.currentUnitIndex ?? 0;
              unitIdToSend =
                courseData?.units?.[idx]?.id ||
                courseData?.units?.[0]?.id ||
                unitIdToSend;
              if (!unitIdToSend || unitIdToSend === 'unknownUnit') {
                console.warn(
                  '[updateCourseProgress] Missing unitId at lessonEnded; using fallback failed',
                );
              } else {
                console.log(
                  '[updateCourseProgress] Using fallback unitId at lessonEnded:',
                  unitIdToSend,
                );
              }
            } catch (_) {
              console.warn(
                '[updateCourseProgress] Failed to derive fallback unitId at lessonEnded',
              );
            }
          }
          dispatch(setLastQuestion(`${payload_variables[1]}|||${Date.now()}`)); // Time is appended to change the redux state to retrigger

          updateCourseProgress({
            courseId: this.props.courseId,
            unitId: unitIdToSend,
            lessonId: this.props.currentLessonProgress.currentLessonId,
            userId: this.props.userId,
            courseProgressState: 100,
            legacy: this.props.legacy,
            token: this.props.token,
            currentLessonSlidesWatched:
              this.props.currentLessonProgress.currentLessonSlidesWatched,
            currentSlideIndex: Math.max(
              0,
              Number(this.props.currentLessonProgress.currentLessonTotalSlides || 0) - 1,
            ),
            currentLessonTimeSpent:
              Date.now() -
              this.props.currentLessonProgress.currentLessonStartTimeStamp,
          }).catch(e =>
            console.warn(
              '[updateCourseProgress] 100% failed',
              e?.message || e,
            ),
          );
        }
        const entry = Date.now(); // Using high-resolution time measurement
        this.setState({
          exitTime: entry,
        });
      } else if (payload === 'courseStarted') {
        const entry = Date.now(); // Using high-resolution time measurement
        this.setState({
          entryTime: entry,
          isStarted: true,
        });
      } else if (payload_variables[0] === 'process_portal_detection') {
        var portalType = payload_variables[1];
        var detectionType = payload_variables[2];
        var curriculumPortalName = payload_variables[3];
        console.error(
          `From React process_portal_detection received ${portalType} ${detectionType} ${curriculumPortalName}`,
        );
        const { dispatch } = this.props;

        if (detectionType == 'exited') {
          dispatch(
            setCourseLeaveConfigs({
              message:
                portalType === 'curriculum_portal'
                  ? 'Are you sure you want to teleport to Dashboard?'
                  : this.props.customCourse
                    ? 'Are you sure you want to go back to Home?'
                    : 'Are you sure you want to teleport to Quikbase?',
              portalType: 'exit_portal',
            }),
          );
          return;
        }

        if (portalType == 'next_unit_portal') {
          dispatch(
            setCourseLeaveConfigs({
              message: 'Are you sure you want to teleport to next Unit?',
              portalType: `${portalType}`,
            }),
          );
          dispatch(setShowConfirmationDialog(true));
        } else if (portalType == 'exit_portal') {
          dispatch(
            setCourseLeaveConfigs({
              message: this.props.customCourse
                ? 'Are you sure you want to go back to Home?'
                : 'Are you sure you want to teleport to Quikbase?',
              portalType: `${portalType}`,
            }),
          );
          dispatch(setShowConfirmationDialog(true));
        } else if (portalType == 'curriculum_portal') {
          const subject = curriculumPortalName.split('_')[0];
          const grade = curriculumPortalName.split('_')[1];
          dispatch(
            setCurriculumGradeAndSubject({ grade: grade, subject: subject }),
          );
          dispatch(setShowUnitSelectionScreen(true));
        }
      } else if (payload_variables[0] === 'process_outline_trigger') {
        var triggerName = payload_variables[1];
        var splittedParams = triggerName.split('_');
        let triggerType = '';
        let subject = '';
        let grade = '';
        const { dispatch } = this.props;

        if (splittedParams[0] == 'leaderboard') {
          triggerType == 'leaderboard';
          subject = splittedParams[1];
          grade = splittedParams[2];
          const webBase = String(WEB_APP_BASE_URL || '').replace(/\/$/, '');
          dispatch(
            enableWebboard(
              `${webBase}/game-leaderboards?token=${this.props.token}`,
            ),
          );
        } else if (splittedParams[0] == 'diagnostic') {
          triggerType == 'diagnostic_test';
          subject = splittedParams[2];
          const subjectInitial = subject[0].toUpperCase();
          grade = splittedParams[3];
          const assessmentBase = String(ASSESSMENT_BASE_URL || '').replace(/\/$/, '');
          dispatch(
            enableWebboard(
              `${assessmentBase}/?user_id=${this.props.userId}&token=${this.props.token}&dt_id=${grade}${subjectInitial}`,
            ),
          );
        } else {
          console.error(
            `could not determine outline trigger type. The payload variables received are ${payload_variables}`,
          );
          return;
        }
      }
    } else {
      onLegacyFromWorld(message);
    }
  };

  appearanceComplete = message => {
    const { onAppearanceCompleteFromWorld } = this.props;
    onAppearanceCompleteFromWorld(message);
  };

  setPowerMode(lowPower) {
    if (lowPower) {
      PlatformWorld.sendMessage({
        route: COMMAND_ROUTE,
        payload: 'set_quality LOW',
      });
      PlatformWorld.sendMessage({
        route: COMMAND_ROUTE,
        payload: 'set_fps_limit 24',
      });
    } else {
      PlatformWorld.sendMessage({
        route: COMMAND_ROUTE,
        payload: 'set_quality HIGH',
      });
      PlatformWorld.sendMessage({
        route: COMMAND_ROUTE,
        payload: 'set_fps_limit 30',
      });
    }

    this.setState({ lowPower: lowPower });
  }

  onLevelLoadComplete = () => {
    const { locationFilter, onLoadComplete, lowPower, showFloatingNames } =
      this.props;
    const effectiveLocationFilter = this.mapLocationAlias(locationFilter);

    PlatformWorld.sendMessage({
      route: COMMAND_ROUTE,
      payload: `set_location_filter ${effectiveLocationFilter}`,
    });
    PlatformWorld.sendMessage({
      route: COMMAND_ROUTE,
      payload: 'finish_loading',
    });
    PlatformWorld.sendMessage({ route: COMMAND_ROUTE, payload: 'after_join' });

    PlatformWorld.sendMessage({
      route: COMMAND_ROUTE,
      payload: `show_names ${showFloatingNames}`,
    });
    PlatformWorld.sendMessage({
      route: COMMAND_ROUTE,
      payload: 'show_chat false',
    });

    this.setPowerMode(lowPower);

    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      PlatformWorld.sendMessage({
        route: COMMAND_ROUTE,
        payload: 'show_thumbstick true',
      });

      const thumbSize = PixelRatio.getPixelSizeForLayoutSize(56);
      const thumbTravel = PixelRatio.getPixelSizeForLayoutSize(112);
      const thumbX = PixelRatio.getPixelSizeForLayoutSize(84);
      const thumbY = PixelRatio.getPixelSizeForLayoutSize(315);
      PlatformWorld.sendMessage({
        route: COMMAND_ROUTE,
        payload: `set_thumbstick ${thumbX} ${thumbY} ${thumbSize} ${thumbTravel}`,
      });
    } else {
      PlatformWorld.sendMessage({
        route: COMMAND_ROUTE,
        payload: `set_legacy ${this.props.legacy}`,
      });
      PlatformWorld.sendMessage({
        route: COMMAND_ROUTE,
        payload: `direct_landing ${this.props.direct_landing}`,
      });

      // One-avatar-everywhere: replay saved Character Lab JSON into the world immediately
      // before set_avatar_url, then ensure set_avatar_url uses the SAME key.
      const rec = this.replaySavedAvatarIntoWorld();
      const avatarUrlToSend =
        rec?.key ? `characterlab_json:${rec.key}` : this.props.newAvatarUrl;

      PlatformWorld.sendMessage({
        route: COMMAND_ROUTE,
        payload: `set_avatar_url ${avatarUrlToSend} ${this.props.direct_landing}`,
      });

      // Mark level as loaded so follow-up prop updates can reconcile portal state.
      if (!this.state.levelLoaded) {
        this.setState({ levelLoaded: true });
      }

      // Ensure next unit portal is OFF for custom courses and for the last unit.
      this.ensureNextUnitPortalState('onLevelLoadComplete');

      if (this.props.customCourse) {
        PlatformWorld.sendMessage({
          route: COMMAND_ROUTE,
          payload: `turnoff_exit_portal true`,
        });
      }
    }
    onLoadComplete();
  };

  render() {
    const { location, style } = this.props;
    const effectiveLocation = this.mapLocationAlias(location);
    const { loading } = this.state;

    return (
      <View pointerEvents="box-none" style={[styles.background, style]}>
        {effectiveLocation && (
          <PlatformWorld
            location={effectiveLocation}
            onTransform={message => this.filterTransformMessages(message)}
            onLegacyMessage={message => this.filterLegacyMessages(message)}
          />
        )}
        <Loading loading={loading} />
        <TextOverlayManager loading={loading} />
        <LabelDisplay loading={loading} />
      </View>
    );
  }
}

World.defaultProps = {
  style: {},
  onTransformFromWorld: () => { },
  onLegacyFromWorld: () => { },
  onLoadComplete: () => { },
  lowPower: false,
  showFloatingNames: false,
};

World.propTypes = {
  style: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.number,
    PropTypes.array,
  ]),
  location: PropTypes.string.isRequired,
  locationFilter: PropTypes.string.isRequired,
  onTransformFromWorld: PropTypes.func,
  onLegacyFromWorld: PropTypes.func,
  onLoadComplete: PropTypes.func,
  lowPower: PropTypes.bool,
  showFloatingNames: PropTypes.bool,
  newAvatarUrl: PropTypes.string,
  userId: PropTypes.string,
  courseId: PropTypes.string,
  courseProgress: PropTypes.string,
  isPromotional: PropTypes.bool,
  legacy: PropTypes.bool,
  currentLessonProgress: PropTypes.shape({
    currentLessonId: PropTypes.string.isRequired,
    currentLessonTotalSlides: PropTypes.number.isRequired,
  }),
  token: PropTypes.string,
  unitId: PropTypes.string,
  courseData: PropTypes.object,
  customCourse: PropTypes.bool,
};

const mapStateToProps = state => ({
  newAvatarUrl: state.meeting.meeting.avatarUrlValue,
  userId: state.app.courseTokenValues.userId,
  courseId: state.app.courseTokenValues.courseId,
  courseProgress: state.app.courseTokenValues.courseProgress,
  isPromotional: state.app.courseTokenValues.isPromotional,
  direct_landing: state.meeting.meeting.direct_landing,
  legacy: state.meeting.meeting.legacy,
  lessonControls: state.meeting.meeting.lessonControls,
  lessonPaused: state.meeting.meeting.lessonPaused,
  currentLessonProgress: state.course.currentLessonProgress,
  token: state.meeting.properties.token,
  unitId: state.app.courseTokenValues.unitId,
  courseData: state.course.courseData,
  customCourse: state.meeting.meeting.customCourse,
});

const styles = StyleSheet.create({
  background: {
    backgroundColor: '#000',
  },
});

export default connect(mapStateToProps)(World);
